import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ReferencePill } from './ComplianceReferencePills.jsx';
import { complianceTypeLabel, getIssueComplianceType } from './complianceIssuePresenters';

const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 120;

/**
 * Pull everything the card needs straight from the issue already in memory.
 * No backend call — the requirement text rides along on the compliance feed
 * (`expectedCompliance`) and/or the merged Requirements Register row
 * (`requirementRegister`), so we never touch the broken /codes/resolve path.
 */
function getRequirementCardData(issue, requirementRef) {
  const reg = issue?.requirementRegister || null;
  const article = String(
    issue?.requirementArticle ||
      reg?.article ||
      requirementRef?.value ||
      requirementRef?.label ||
      ''
  ).trim();
  const text = String(reg?.requirementText || issue?.expectedCompliance || '').trim();
  const typeKey = getIssueComplianceType(issue) || reg?.complianceType || '';
  const typeLabel = typeKey ? complianceTypeLabel(typeKey) : '';
  const sourceDoc = String(reg?.sourceDocName || '').trim();
  const pageNumber = reg?.pageNumber ?? null;
  return { article, text, typeLabel, sourceDoc, pageNumber };
}

function RequirementHoverCard({ anchorRect, data, pinned, onClose, onMouseEnter, onMouseLeave }) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRect) return;
    const card = cardRef.current;
    const cardWidth = card?.offsetWidth || 340;
    const cardHeight = card?.offsetHeight || 160;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Default: open just below the pill; flip above if it would overflow the viewport bottom.
    let top = anchorRect.bottom + 6;
    if (top + cardHeight > vh - margin) {
      top = Math.max(margin, anchorRect.top - cardHeight - 6);
    }
    // Clamp horizontally to the viewport.
    let left = anchorRect.left;
    if (left + cardWidth > vw - margin) {
      left = Math.max(margin, vw - cardWidth - margin);
    }
    if (left < margin) left = margin;

    setPos({ top, left });
  }, [anchorRect, data]);

  if (!anchorRect) return null;

  const card = (
    <div
      ref={cardRef}
      className={`requirement-hovercard${pinned ? ' requirement-hovercard--pinned' : ''}`}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
      role="dialog"
      aria-label={`Requirement ${data.article || ''}`.trim()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="requirement-hovercard__header">
        <span className="requirement-hovercard__article">{data.article || 'Requirement'}</span>
        {data.typeLabel ? (
          <span className="requirement-hovercard__chip">{data.typeLabel}</span>
        ) : null}
        {pinned ? (
          <button
            type="button"
            className="requirement-hovercard__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        ) : null}
      </div>
      <div className="requirement-hovercard__body">
        {data.text ? (
          data.text
        ) : (
          <span className="requirement-hovercard__hint">
            Requirement text not available — open the Requirements Register.
          </span>
        )}
      </div>
      {data.sourceDoc || data.pageNumber ? (
        <div className="requirement-hovercard__footer">
          {data.sourceDoc ? (
            <span className="requirement-hovercard__source" title={data.sourceDoc}>
              {data.sourceDoc}
            </span>
          ) : null}
          {data.pageNumber ? (
            <span className="requirement-hovercard__page">p. {data.pageNumber}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return createPortal(card, document.body);
}

/**
 * Table-cell wrapper around the requirement ReferencePill. Owns the hover +
 * click-to-pin interaction and renders a non-blocking, portal-positioned card.
 * Intentionally does NOT route to the PDF-resolve path.
 */
export function RequirementRefCell({ issue, requirementRef }) {
  const anchorRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const label = String(requirementRef?.label || requirementRef?.value || '').trim();
  const data = getRequirementCardData(issue, requirementRef);

  const clearTimers = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const captureRect = useCallback(() => {
    const el = anchorRef.current;
    if (el) setAnchorRect(el.getBoundingClientRect());
  }, []);

  const scheduleOpen = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (open || pinned) return;
    openTimer.current = setTimeout(() => {
      captureRect();
      setOpen(true);
    }, OPEN_DELAY_MS);
  }, [open, pinned, captureRect]);

  const scheduleClose = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (pinned) return;
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [pinned]);

  const togglePin = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      clearTimers();
      captureRect();
      setPinned((prev) => {
        const next = !prev;
        setOpen(next);
        return next;
      });
    },
    [clearTimers, captureRect]
  );

  const handleClose = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      clearTimers();
      setPinned(false);
      setOpen(false);
    },
    [clearTimers]
  );

  // Click-away + Escape dismiss while pinned.
  useEffect(() => {
    if (!pinned) return undefined;
    const onDocMouseDown = (e) => {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.requirement-hovercard')) return;
      handleClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned, handleClose]);

  // Keep the card glued to the pill while it's open and the page scrolls/resizes.
  useEffect(() => {
    if (!open && !pinned) return undefined;
    const onReflow = () => captureRect();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, pinned, captureRect]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (!label) {
    return <span className="td-ref-pill">—</span>;
  }

  return (
    <span
      ref={anchorRef}
      className="requirement-ref-cell"
      role="button"
      tabIndex={0}
      aria-label={`Show requirement ${label}`}
      aria-expanded={open || pinned}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={() => {
        captureRect();
        setOpen(true);
      }}
      onBlur={() => {
        if (!pinned) setOpen(false);
      }}
      onClick={togglePin}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          togglePin(e);
        } else if (e.key === 'Escape') {
          handleClose(e);
        }
      }}
    >
      <ReferencePill reference={requirementRef} variant="table" />
      {open || pinned ? (
        <RequirementHoverCard
          anchorRect={anchorRect}
          data={data}
          pinned={pinned}
          onClose={handleClose}
          onMouseEnter={() => {
            if (closeTimer.current) {
              clearTimeout(closeTimer.current);
              closeTimer.current = null;
            }
          }}
          onMouseLeave={scheduleClose}
        />
      ) : null}
    </span>
  );
}

export default RequirementRefCell;
